import { AppRoutes } from "@/shared/lib/routes"
import { createRoute } from "@tanstack/react-router"
import { rootRoute } from "./__root"
import { LegalLayout } from "@/shared/components/legal-layout"
import { LegalLink } from "@/shared/components/legal-link"

export const cookiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AppRoutes.COOKIES,
  component: CookiesPage,
})

function CookiesPage() {
  return (
    <LegalLayout title="Cookie Policy">
      <section>
        <p>
          Please read this Cookie Policy carefully to understand how astrology.clinic (“we”, “us”,
          “our”) uses cookies and similar tracking technologies when you visit our website at
          astrology.clinic or use our applications and related services (collectively, the
          “Services”).
        </p>
        <p>
          By continuing to use the Services, you acknowledge that you have read and understood this
          Cookie Policy. Tracking technologies are active by default when you use our Services
          (opt-out model). You may opt out at any time using the mechanisms described in this policy.
          If you do not agree to our use of cookies, you should adjust your settings or refrain from
          using the Services.
        </p>
      </section>

      <section>
        <h2>WHAT ARE COOKIES?</h2>
        <p>
          Cookies are small text files that are placed on your device (computer, smartphone, tablet)
          when you visit a website or use an application. Cookies are widely used by website and
          application operators to make their services work more efficiently, as well as to provide
          analytical information.
        </p>
        <p>
          In addition to cookies, we may use similar tracking technologies such as web beacons, pixel
          tags, local storage objects, and software development kits (SDKs). Throughout this Cookie
          Policy, we refer to all such technologies collectively as “cookies.” Under applicable US
          state privacy laws, including the California Consumer Privacy Act as amended by the
          California Privacy Rights Act (CCPA/CPRA), information collected through cookies may
          constitute “personal information” or “personal data” where it can be linked, directly or
          indirectly, to a specific consumer, household, or device.
        </p>
      </section>

      <section>
        <h2>WHAT TYPES OF COOKIES DO WE USE?</h2>
        <p>We use the following categories of cookies in our Services:</p>
        <ul>
          <li>
            Strictly Necessary Cookies – these cookies are essential for the Services to function
            properly. They enable core features such as security, session management, and access to
            certain areas of the Services. Without these cookies, the Services cannot be provided.
            These cookies do not collect any information that could be used for marketing purposes.
          </li>
          <li>
            Analytical / Performance Cookies – these cookies allow us to collect information about how
            you interact with the Services, such as which pages you visit most frequently and whether
            you encounter any errors. We use this information to improve the way the Services work.
          </li>
          <li>
            Functionality Cookies – these cookies allow the Services to remember choices you make
            (such as your language preference or time zone) and provide enhanced, more personalized
            features.
          </li>
          <li>
            Targeting / Advertising Cookies – these cookies are used to deliver advertisements that
            are more relevant to your interests. They are placed by us or our advertising partners and
            are used to limit ad frequency and measure the effectiveness of campaigns. Where such
            cookies enable third-party ad networks to access user data for cross-context behavioral
            advertising, this may constitute “sharing” of personal information under CCPA/CPRA. You
            have the right to opt out of such sharing (see “Your Privacy Rights and Opt-Out Options”
            below).
          </li>
        </ul>
      </section>

      <section>
        <h2>FOR WHAT PURPOSES DO WE USE COOKIES?</h2>
        <p>We use cookies for the following purposes:</p>
        <ul>
          <li>to ensure the proper functioning and security of the Services;</li>
          <li>to remember your preferences and settings across sessions;</li>
          <li>to analyze how you use the Services in order to improve their performance and content;</li>
          <li>to measure the effectiveness of our advertising campaigns;</li>
          <li>
            to deliver personalized advertisements based on your interests and browsing behavior;
          </li>
          <li>to prevent fraudulent activity and ensure the safety of our users;</li>
          <li>to comply with legal obligations applicable to us.</li>
        </ul>
      </section>

      <section>
        <h2>DO THIRD PARTIES PLACE COOKIES THROUGH OUR SERVICES?</h2>
        <p>
          Yes. In connection with the purposes described above, we work with third-party analytics
          and advertising partners who may place cookies on your device through our Services. These
          third parties process the data collected by their cookies in accordance with their own
          privacy and cookie policies. We do not control the cookies set by third parties and are not
          responsible for their content or accuracy.
        </p>
        <p>
          Such third-party partners may include, but are not limited to, providers of analytics
          services, advertising networks, and social media platforms. Please note that providing
          personal information to these third parties through advertising or analytics cookies may
          constitute a “sale” or “sharing” of personal information under applicable US state privacy
          laws. You have the right to opt out of such practices as described below.
        </p>
      </section>

      <section>
        <h2>HOW LONG DO COOKIES REMAIN ON YOUR DEVICE?</h2>
        <p>
          Cookies may remain on your device for varying periods of time, depending on their type:
        </p>
        <ul>
          <li>
            Session Cookies – these cookies are temporary and are automatically deleted when you close
            your browser or application. They are used to maintain continuity during a single browsing
            session.
          </li>
          <li>
            Persistent Cookies – these cookies remain on your device for a set period of time
            specified in the cookie, or until you delete them manually. They are activated each time
            you visit the Services that placed that particular cookie.
          </li>
        </ul>
      </section>

      <section>
        <h2>YOUR PRIVACY RIGHTS AND OPT-OUT OPTIONS</h2>
        <p>
          Under applicable US state privacy laws, you have the right to opt out of the sale and
          sharing of your personal information, including through the use of tracking cookies. We do
          not require you to opt in before collecting data through cookies - however, you may opt out
          at any time using the methods below.
        </p>
      </section>

      <section>
        <h2>DO NOT SELL OR SHARE MY PERSONAL INFORMATION</h2>
        <p>
          You have the right to direct us not to sell or share your personal information with third
          parties for cross-context behavioral advertising. To exercise this right, please contact us
          at{" "}
          <a href="mailto:support@devroqapps.com" className="underline hover:text-foreground">
            support@devroqapps.com
          </a>{" "}
          with the subject line “Do Not Sell or Share My Personal Information.” We will process your
          request without undue delay.
        </p>
        <p>
          <strong>Global Privacy Control (GPC)</strong>
        </p>
        <p>
          We recognize and honor the Global Privacy Control (GPC) signal, a browser-based mechanism
          that communicates your preference to opt out of the sale or sharing of your personal data.
          If your browser or device has GPC enabled, we will treat it as a valid opt-out request under
          applicable state laws, including CCPA/CPRA. We will notify you that your GPC signal has been
          received and honored. Please note that honoring GPC does not prevent us from using strictly
          necessary cookies.
        </p>
        <p>
          <strong>Additional Rights for Residents of Certain US States</strong>
        </p>
        <p>
          Depending on the state in which you reside, you may have the following rights with regard to
          personal information collected through cookies:
        </p>
        <ul>
          <li>
            California (CCPA/CPRA): right to know, right to delete, right to correct, right to opt out
            of sale/sharing, right to limit use of sensitive personal information, right to
            non-discrimination for exercising privacy rights.
          </li>
          <li>
            Colorado (CPA), Virginia (VCDPA), Connecticut (CTDPA): right to opt out of targeted
            advertising, right to access, correct, and delete personal data, right to data
            portability.
          </li>
          <li>
            Texas (TDPSA), Utah (UCPA), Florida (FDBR), Minnesota (MCDPA): right to opt out of the sale
            of personal data and targeted advertising, right to access and delete personal data.
          </li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at{" "}
          <a href="mailto:support@devroqapps.com" className="underline hover:text-foreground">
            support@devroqapps.com
          </a>{" "}
          We will respond within the timeframe required by applicable law.
        </p>
      </section>

      <section>
        <h2>CHILDREN’S PRIVACY AND COOKIES</h2>
        <p>
          Our Services are intended for users aged 16 and over. We do not knowingly use cookies to
          collect personal information from children under the age of 13. In compliance with the
          federal Children’s Online Privacy Protection Act (COPPA), we do not collect, use, or
          disclose personal information from children under 13 without verifiable parental consent.
        </p>
        <p>
          Under CCPA/CPRA, information relating to consumers under the age of 16 is treated as
          sensitive personal information. We do not knowingly share the personal information of users
          under 16 with third parties for targeted advertising purposes without the affirmative
          opt-in consent of the user (for users aged 13–16) or their parent or guardian (for users
          under 13).
        </p>
        <p>
          If you believe we have inadvertently collected personal information from a child under 13,
          please contact us immediately at{" "}
          <a href="mailto:support@devroqapps.com" className="underline hover:text-foreground">
            support@devroqapps.com
          </a>{" "}
          and we will take prompt steps to delete such information.
        </p>
      </section>

      <section>
        <h2>HOW CAN YOU CONTROL COOKIES?</h2>
        <p>
          In addition to the opt-out mechanisms described above, you may control cookies through the
          following means:
        </p>
        <p>
          <strong>Browser Settings.</strong> Most web browsers allow you to control cookies through
          their settings. You can set your browser to refuse all cookies, to notify you when a cookie
          is being set, or to delete cookies that have already been set. Please note that if you
          choose to block or delete cookies, some features of the Services may not function as
          intended. Note that closing or navigating away from a consent notice without making an
          active choice does not constitute consent or refusal under applicable law.
        </p>
        <p>
          <strong>Device Settings.</strong> You may also control advertising-related cookies through
          your device settings:
        </p>
        <ul>
          <li>
            iOS operating system: enable Limit Ad Tracking on iOS by opening “Settings,” then tapping
            “Privacy,” then “Advertising”, and sliding the Limit Ad Tracking switch to “On”.
          </li>
          <li>
            Android operating system: opt out of Ads Personalization on Android by opening “Settings”,
            then tapping “Google”, then “Ads”, then enabling the switch next to “Opt out of Ads
            Personalization”.
          </li>
        </ul>
        <p>
          Please note that restricting cookies does not mean you will no longer see advertisements; it
          means that the advertisements you see will be less tailored to your interests.
        </p>
      </section>

      <section>
        <h2>DO WE USE COOKIES TO PROCESS PERSONAL DATA?</h2>
        <p>
          Some cookies we use may collect information that, alone or in combination with other
          information, constitutes Personal Information as defined under applicable US state privacy
          laws or Personal Data as defined under GDPR. Where cookies are used to process such
          information, such processing is carried out in accordance with our Privacy Policy, which is
          available at{" "}
          <LegalLink to={AppRoutes.PRIVACY} className="underline hover:text-foreground">
            astrology.clinic/privacy
          </LegalLink>
          .
        </p>
      </section>

      <section>
        <h2>RELATIONSHIP WITH OUR PRIVACY POLICY</h2>
        <p>
          This Cookie Policy forms part of and should be read together with our Privacy Policy
          available at{" "}
          <LegalLink to={AppRoutes.PRIVACY} className="underline hover:text-foreground">
            astrology.clinic/privacy
          </LegalLink>
          . The Privacy Policy contains additional information about our data processing practices,
          your rights under applicable law, and how to contact us to exercise those rights.
        </p>
      </section>

      <section>
        <h2>DO WE CONTROL THIRD-PARTY WEBSITES?</h2>
        <p>
          The Services may contain links to other websites not operated by us. If you click on such a
          link, you will be directed to that third party’s site. We strongly recommend that you review
          the privacy and cookie policies of every website you visit. Under no circumstances do we
          control third-party websites or assume any responsibility for their content, privacy
          policies, or cookie practices.
        </p>
      </section>

      <section>
        <h2>CHANGES TO THIS COOKIE POLICY</h2>
        <p>
          We may update this Cookie Policy from time to time to reflect changes in the technologies we
          use, applicable legal requirements, or our business practices. We encourage you to review
          this Cookie Policy periodically. If we make material changes, we may notify you by posting a
          prominent notice on the Services before the changes take effect and by updating the “Last
          Updated” date at the top of this Cookie Policy.
        </p>
        <p>
          Your continued use of the Services after any changes to this Cookie Policy constitutes your
          acceptance of the updated policy.
        </p>
      </section>

      <section>
        <h2>CONTACT US</h2>
        <p>
          If you have any questions about this Cookie Policy, wish to opt out of the sale or sharing
          of your personal information, or wish to exercise any of your privacy rights, please contact
          us at:{" "}
          <a href="mailto:support@devroqapps.com" className="underline hover:text-foreground">
            support@devroqapps.com
          </a>
          .
        </p>
      </section>
    </LegalLayout>
  )
}
